/**
 * Request — the single HTTP wrapper every module api.js goes through.
 *
 * Adds the backend base URL (REACT_APP_API_URI from .env, baked in at build
 * time) and the JWT from the auth cookie on every call. Never throws:
 * resolves to { response } on success or { error } on failure, so callers
 * always destructure both and check which one is set.
 *
 * SECURITY: this is also where a dead session is detected. It sees every REST
 * call — measured 2026-09-15: no other axios, fetch or XMLHttpRequest call to
 * our API exists in frontend/src (api/getPlaces.js uses fetch, but against
 * Mapbox, not us).
 *
 * ⚠️ IT IS NOT THE ONLY TRANSPORT, and the distinction matters to anyone
 * reasoning about coverage from this file. ZonesDetails.jsx:114 opens a
 * socket.io connection to the same API and authenticates the handshake with the
 * same JWT. A rejected handshake is a dead session this file cannot see. It
 * happens to be covered because that page also dispatches doDetailZone() on the
 * same mount, and THAT 401 arrives here — an accident of ordering, not a
 * guarantee.
 *
 * The first version of this comment claimed no other call to our API existed
 * anywhere in frontend/src. The search behind it was for
 * `axios|fetch(|XMLHttpRequest`, and socket.io hides its transport behind a
 * library import, so the pattern could not match what was there. Calibrating
 * the instrument proved it could find those three spellings — not that those
 * three spellings were the question. Found by review, 2026-09-15.
 */
import axios from "axios";
import Cookies from "js-cookie";
import { TOKEN_NAME, SESSION_EXPIRED_KEY } from "../constants";

/**
 * The backend's @Public() endpoints (auth.controller.ts). A 401 from one of
 * these means something OTHER than "your session ended":
 *
 *   /auth/login                      wrong password
 *   /auth/register                   nginx's basic-auth stopgap (GF-38)
 *   /auth/reset-password             public by the same decorator
 *   /auth/confirm-reset              public by the same decorator
 *
 * An explicit list, NOT a "/auth/" prefix test, and the difference is the whole
 * point: a future endpoint under /auth/ that DOES require a token — /auth/me,
 * say — would fall through a prefix test and never log anyone out, silently.
 * Adding a @Public() route to the backend means adding a line here; adding a
 * protected one means doing nothing. The failure mode is therefore a spurious
 * logout (loud, reported in a day) rather than a session that never ends
 * (quiet, found by nobody).
 */
const PUBLIC_ENDPOINTS = [
  "/auth/login",
  "/auth/register",
  "/auth/reset-password",
  "/auth/confirm-reset",
];

/**
 * Every spelling that reaches the same backend handler must land on the same
 * answer here, because the cost of a miss is a user thrown out to /login for
 * mistyping a password. Three axes, each one measured rather than assumed:
 *
 *  - case and trailing slash: Express routes case-insensitively and ignores a
 *    trailing slash — measured 2026-09-10 during GF-38, when /AUTH/REGISTER/
 *    was shown to reach the same handler as /auth/register.
 *  - query and hash: never part of the route.
 *  - leading slash: axios joins baseURL and a slashless path with one anyway,
 *    so request("auth/login") hits the same handler as request("/auth/login").
 *
 * The leading-slash case has no caller today — all 24 call sites across the six
 * importing files were checked 2026-09-15 and every one spells it with the
 * slash. It is normalised anyway, because "correct as long as everyone
 * remembers" is not a property worth having in a security gate. Found latent by
 * review, 2026-09-15.
 */
const isPublicEndpoint = (endpoint) => {
  const path = String(endpoint || "")
    .split(/[?#]/)[0]
    .replace(/\/+$/, "")
    .toLowerCase();
  // Add a leading slash when one is missing; do NOT collapse several. "//x" is
  // a protocol-relative URL to axios (isAbsoluteURL matches "//"), so baseURL is
  // not applied and the request never reaches our API at all — folding it into
  // the public list would be the one branch here that treats a NON-equivalent
  // spelling as public, in a gate whose failure mode is supposed to be a noisy
  // false logout rather than a silent missed one.
  return PUBLIC_ENDPOINTS.includes(path.startsWith("/") ? path : `/${path}`);
};

// A single page has many requests in flight — the zone view alone fires
// several — and every one of them would otherwise start its own navigation.
let sessionExpiryHandled = false;

// ...but module scope is NOT quite the same thing as "one page load". A bfcache
// restore — pressing Back after the logout — brings the page back with its whole
// JS heap intact: this flag still true, Redux still `authenticated`, menus and
// lists still rendered. The next 401 would then hit the early return and do
// nothing, which is the exact pre-GF-50 symptom, made permanent for that page
// instance.
//
// Whether these pages are bfcache-eligible at all is genuinely unclear —
// ZonesDetails holds an open socket.io connection, and browsers differ. Rather
// than measure something that varies by browser and by page, reset the flag on
// a persisted pageshow: correct either way, and cheaper than the measurement
// would have been. Review finding, 2026-09-15.
if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) sessionExpiryHandled = false;
  });
}

/**
 * GF-50: the API no longer accepts our token — it expired, the account was
 * deleted, or JWT_SECRET was rotated. Before this existed the app kept running
 * as if nothing happened: `authenticated` is derived from the mere PRESENCE of
 * the cookie and never verified, so menus, lists and dialogs all stayed usable
 * and every action failed with a generic "Unauthorized" toast. Measured in
 * staging 2026-09-11 by deleting a logged-in account mid-session.
 */
const handleExpiredSession = () => {
  if (sessionExpiryHandled) return;
  sessionExpiryHandled = true;

  Cookies.remove(TOKEN_NAME);
  try {
    sessionStorage.setItem(SESSION_EXPIRED_KEY, "1");
  } catch (e) {
    // Blocked or full storage. The redirect must happen regardless; the user
    // then reaches the login page without the explanation, which is still
    // strictly better than the app that looks alive and refuses everything.
  }

  // A full page load, NOT a router navigation. The session is gone, so every
  // module's in-memory copy of the user's data has to go with it — a
  // client-side route change would keep all of it. The auth slice derives
  // `authenticated` from the cookie at init, so the reload lands in the right
  // state on its own. That also avoids importing the Redux store here, which
  // would close a cycle: store -> slices -> thunks -> api -> request -> store.
  window.location.assign("/login");
};

/**
 * opts.unattended — "no human is waiting for this response".
 *
 * The test is about the USER, not about the technical call stack, and the two
 * do not coincide: a request fired because a page is loading is ATTENDED even
 * though no click caused it, because the user is sitting there watching a page
 * fill itself in. A timer-driven poll is unattended.
 *
 * Only unattended requests are exempt from the logout in the catch below. The
 * reason is MaintenanceBanner, which polls every 60 s from MainLayout — i.e. on
 * every protected page. Without the exemption, a token that dies while someone
 * is typing an address would reload the page out from under them within the
 * minute and take the unsaved text with it, with no action of theirs to explain
 * it. With it, the logout happens on their next real action — which is normally
 * the save itself.
 *
 * DEFAULT IS ATTENDED, deliberately. A new poll that someone forgets to flag
 * logs people out mid-work: annoying, visible, reported within a day. The
 * opposite default fails silently and leaves sessions that never end.
 */
const Request = async (endpoint, opts) => {
  const { headers, unattended, ...options } = opts;
  const config = Object.assign(
    {
      url: endpoint,
      baseURL: process.env.REACT_APP_API_URI,
      headers: Object.assign(
        {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get(TOKEN_NAME)}`,
        },
        headers
      ),
      timeout: 60000,
    },
    options
  );
  try {
    const response = await axios.request(config);
    return { response: response.data || {} };
  } catch (error) {
    if (error.response) {
      if (
        error.response.status === 401 &&
        !isPublicEndpoint(endpoint) &&
        !unattended
      ) {
        handleExpiredSession();
      }
      // The error is still returned even when a logout was just started.
      // window.location.assign() does not stop the current JavaScript task, so
      // the caller resumes either way — and a caller left awaiting a promise
      // that never settles would hang the UI during the moments before the
      // page actually goes away.
      return {
        error: Object.assign(error.response.data, {
          status: error.response.status || error.request?.status,
        }),
      };
    }
    if (error.request && error.request._response) {
      return { error: { message: error.request._response } };
    }
    return { error: { message: error.message } };
  }
}

export default Request;
